import { HttpClient } from '@angular/common/http';
import {
  AccessDeniedError,
  DisplayEntity,
  Entity,
  EntityDetails,
  Field,
  Key,
  ListItem,
  Octopus,
} from '@maximizer/core/shared/domain';
import { Observable, map, of, switchMap } from 'rxjs';
import { ContextService } from './context.service';
import { MetadataService } from './metadata.service';

export abstract class EntityService {
  readonly MetadataScope: Octopus.Scope<Octopus.Metadata> = {
    Key: {
      Value: 1,
    },
    Name: 1,
    Description: 1,
    Alias: 1,
    AppliesTo: 1,
    Type: 1,
    Assignable: 1,
    Inactive: 1,
    Sortable: 1,
    Queryable: 1,
    HasOption: 1,
    ReadOnly: 1,
    Nullable: 1,
    Attributes: 1,
    Folder: 1,
    Path: 1,
    Mandatory: 1,
    Formula: 1,
    SortValue: 1,
    Tag: 1,
  };

  constructor(
    protected entity: Octopus.EntityCode,
    protected http: HttpClient,
    protected context: ContextService,
    protected metadata: MetadataService,
  ) {}

  abstract get readDriver(): Octopus.RequestConfiguration;

  abstract get writeDriver(): Octopus.RequestConfiguration;

  getKeyAndDisplayScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.KeyAndDisplay> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Key: 1,
      DisplayValue: 1,
    };
  }

  getCollectionScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.Collection<Octopus.KeyAndDisplay>> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Items: [
        {
          Key: 1,
          DisplayValue: 1,
        },
      ],
      DisplayValue: 1,
    };
  }

  getPartnerCompetitorScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.Collection<Octopus.PartnerCompetitor>> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Items: [
        {
          Key: 1,
          DisplayValue: 1,
          Comment: 1,
        },
      ],
      DisplayValue: 1,
    };
  }

  getValueAndDisplayScope<T = unknown>(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.ValueAndDisplay<T>> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Value: 1,
      DisplayValue: 1,
    };
  }

  getAddressKeyScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.AddressKey> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Value: 1,
      Number: 1,
      ID: 1,
      EntityType: 1,
    };
  }

  getCurrencyScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.CurrencyValue> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Value: 1,
      CurrencyCode: 1,
      DisplayValue: 1,
    };
  }

  getPhoneScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.EntityPhone> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Description: this.getValueAndDisplayScope<string>(metadata),
      Number: this.getValueAndDisplayScope<string>(metadata),
      Extension: this.getValueAndDisplayScope<string>(metadata),
      DisplayValue: 1,
    };
  }

  getEmailScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.EntityEmail> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Default: this.getValueAndDisplayScope<boolean>(metadata),
      Description: this.getValueAndDisplayScope<string>(metadata),
      Address: this.getValueAndDisplayScope<string>(metadata),
      DisplayValue: 1,
    };
  }

  getAddressScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.Address<Octopus.EntityAddress>> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Key: this.getAddressKeyScope(metadata),
      Description: this.getValueAndDisplayScope<string>(metadata),
      AddressLine1: this.getValueAndDisplayScope<string>(metadata),
      AddressLine2: this.getValueAndDisplayScope<string>(metadata),
      City: this.getValueAndDisplayScope<string>(metadata),
      Country: this.getValueAndDisplayScope<string>(metadata),
      StateProvince: this.getValueAndDisplayScope<string>(metadata),
      ZipCode: this.getValueAndDisplayScope<string>(metadata),
      Default: this.getValueAndDisplayScope<boolean>(metadata),
      DisplayValue: 1,
    };
  }

  getSecurityAccessScope(
    metadata = true,
  ): Octopus.ScopeWithMetadata<Octopus.EntitySecurityAccess> {
    return {
      $Metadata: metadata ? this.MetadataScope : undefined,
      Read: this.getCollectionScope(metadata),
      Write: this.getCollectionScope(metadata),
    };
  }

  getScopeForFields(fields: Field[]): Octopus.Scope<Entity> {
    const scope: Octopus.Scope<Entity> = {};
    fields.forEach((field) => {
      switch (field.metadata?.type) {
        case Octopus.FieldType.CurrencyField:
          scope[field.id] = this.getCurrencyScope(false);
          break;
        case Octopus.FieldType.AbEntryObject:
        case Octopus.FieldType.AbEntryKey:
        case Octopus.FieldType.CampaignObject:
        case Octopus.FieldType.SalesTeamObject:
        case Octopus.FieldType.SalesProcessObject:
        case Octopus.FieldType.SalesProcessSetupObject:
        case Octopus.FieldType.SalesStageObject:
        case Octopus.FieldType.SalesStageSetupObject:
        case Octopus.FieldType.TerritoryObject:
        case Octopus.FieldType.SecAccessField:
          scope[field.id] = this.getKeyAndDisplayScope(false);
          break;
        case Octopus.FieldType.EnumFieldOfPartnerCompetitor:
          scope[field.id] = this.getPartnerCompetitorScope(false);
          break;
        default:
          scope[field.id] = this.getValueAndDisplayScope(false);
          break;
      }
    });
    return scope;
  }

  protected getTypesScope(metadata = true): Octopus.TypeScope {
    return {
      AbEntryObject: this.getKeyAndDisplayScope(metadata),
      'EnumField<StringItem>': this.getCollectionScope(metadata),
      'EnumField<UidKey>': this.getCollectionScope(metadata),
      SecAccess2LvlField: this.getSecurityAccessScope(metadata),
      '*': this.getValueAndDisplayScope(metadata),
    };
  }

  getTypesScopeForValidation(): Octopus.TypeScope {
    return {
      SalesProcessSetupObject: {
        Description: 1,
      },
      SalesStageSetupObject: {
        Description: 1,
      },
      '*': 1,
    };
  }

  getEntityCodeAndDriver(key: Key): {
    code: Octopus.EntityCode;
    configuration: Octopus.RequestConfiguration;
  } | null {
    switch (key.type) {
      case Octopus.KeyType.AbEntry:
      case Octopus.KeyType.Company:
      case Octopus.KeyType.Contact:
      case Octopus.KeyType.Individual:
        return {
          code: Octopus.AbEntryEntityCode,
          configuration: Octopus.AbEntryReadDriver,
        };

      case Octopus.KeyType.Appointment:
        return {
          code: Octopus.AppointmentEntityCode,
          configuration: Octopus.AppointmentReadDriver,
        };

      case Octopus.KeyType.CSCase:
        return {
          code: Octopus.CaseEntityCode,
          configuration: Octopus.CaseReadDriver,
        };

      case Octopus.KeyType.Opportunity:
        return {
          code: Octopus.OpportunityEntityCode,
          configuration: Octopus.OpportunityReadDriver,
        };

      case Octopus.KeyType.Task:
        return {
          code: Octopus.TaskEntityCode,
          configuration: Octopus.TaskReadDriver,
        };

      case Octopus.KeyType.Lead:
        return {
          code: Octopus.LeadEntityCode,
          configuration: Octopus.LeadReadDriver,
        };

      default:
        return null;
    }
  }

  read(key: string, types: Octopus.TypeScope): Observable<Entity | null> {
    const request: Octopus.EntityRequest = this.getReadRequest(
      {
        SearchQuery: {
          Key: {
            $EQ: key,
          },
        },
      },
      types,
      false,
    );

    return this.http
      .post<Octopus.EntityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((response) => {
          if (
            response.Code === Octopus.ResponseStatusCode.Successful &&
            response[this.entity]?.Data?.length
          ) {
            return response[this.entity]?.Data[0] ?? null;
          }
          return null;
        }),
      );
  }

  forDisplay(key: string): Observable<DisplayEntity | null> {
    const types = this.getTypesScope(false);

    return this.read(key, types).pipe(
      map((entity) => {
        if (entity) {
          return Object.entries(entity).reduce((values, [id, value]) => {
            values[id] =
              ((value as Entity)?.[
                Octopus.EntityFields.DisplayValue
              ] as string) ?? '';
            return values;
          }, {} as DisplayEntity);
        }
        return null;
      }),
    );
  }

  forValidation(key: string): Observable<Entity | null> {
    const types = this.getTypesScopeForValidation();
    return this.read(key, types);
  }

  forReadWithMetadata<T>(key: string): Observable<EntityDetails> {
    const types = this.getTypesScope(true);
    const request: Octopus.EntityRequest = this.getReadRequest(
      {
        SearchQuery: {
          Key: {
            $EQ: key,
          },
        },
      },
      types,
      true,
    );

    return this.getValueAndFields(request, this.entity);
  }

  readFromParent(parentKey: string): Observable<Octopus.Entity[] | undefined> {
    const types = this.getTypesScope(false);
    const request: Octopus.EntityRequest = this.getReadRequest(
      {
        SearchQuery: {
          ParentKey: {
            $EQ: parentKey,
          },
        },
      },
      types,
      false,
    );

    return this.http
      .post<Octopus.EntityResponseWithMetadata>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            return response[this.entity]?.Data;
          }
          return [];
        }),
      );
  }

  readAll(top = 100): Observable<Octopus.Entity[] | undefined> {
    const types = this.getTypesScope(false);
    const request: Octopus.EntityRequest = this.getReadRequest(
      {
        SearchQuery: {},
        Top: top,
      },
      types,
      false,
    );

    return this.http
      .post<Octopus.EntityResponseWithMetadata>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            return response[this.entity]?.Data;
          }
          return [];
        }),
      );
  }

  defaultEntry(intent?: string): Observable<EntityDetails> {
    const defaultEntry = this.entity + 'Default';
    const request: Octopus.EntityRequest = {
      [defaultEntry]: {
        Criteria: {
          SearchQuery: {
            DefaultEntryKey: { $EQ: Octopus.CurrentUser },
          },
        },
        Scope: {
          Fields: this.getFieldsForDefaultEntry(true),
          Types: this.getTypesScope(true),
        },
        Options: {
          All: true,
          Intent: intent ?? 'Create',
        },
      },
      Configuration: this.readDriver,
      Globalization: Octopus.DefaultGlobalization,
    };
    return this.getValueAndFields(request, defaultEntry);
  }

  validate(entity: Entity): Observable<{
    required: string[];
  }> {
    const request: Octopus.EntityWriteRequest = {
      [this.entity]: {
        Data: entity,
      },
      Configuration: this.writeDriver,
    };

    return this.http
      .post<Octopus.EntityValidationResponse>(
        `${this.context.api}${Octopus.Action.VALIDATE}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            const required: string[] = [];
            const validation = response[this.entity]?.Validation;
            if (validation) {
              Object.keys(validation).forEach((key) => {
                const rules = validation[key];
                if (rules?.Mandatory) {
                  required.push(key);
                } else if (rules instanceof Object) {
                  Object.keys(rules).forEach((childKey) => {
                    const rule = (
                      rules as { [key: string]: { Mandatory?: boolean } }
                    )[childKey];
                    if (rule?.Mandatory) {
                      required.push(key + '/' + childKey);
                    }
                  });
                }
              });
            }
            return { required };
          }
          return { required: [] };
        }),
      );
  }

  duplicateCheck(entity: Entity): Observable<Entity[]> {
    const request: Octopus.EntityWriteRequest = {
      [this.entity]: {
        Data: entity,
      },
      Configuration: {
        Drivers: {
          IAbEntryAccess: 'Maximizer.Model.Access.Sql.AbEntryAccess',
        },
      },
    };

    return this.http
      .post<Octopus.EntityDuplicateCheckResponse>(
        `${this.context.api}${Octopus.Action.DuplicateCheck}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            const responseData = response[this.entity];
            if (responseData?.Result?.HasDuplicates) {
              const entityData =
                (responseData.Data as unknown as Entity[]) ?? [];
              return entityData;
            }
          }
          return [];
        }),
      );
  }

  save(keyField: string, entity: Entity): Observable<string | null> {
    const request: Octopus.EntityWriteRequest = {
      [this.entity]: {
        Data: entity,
        Options: {
          IgnoreDefault: true,
          ReadAfterSave: false,
        },
      },
      Configuration: this.writeDriver,
    };

    return this.http
      .post<Octopus.EntityValidationResponse>(
        `${this.context.api}${
          entity[keyField] ? Octopus.Action.UPDATE : Octopus.Action.CREATE
        }`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === Octopus.ResponseStatusCode.Successful) {
            return (response[this.entity]?.Data[keyField] as string) ?? null;
          } else if (
            response.Code === Octopus.ResponseStatusCode.AccessDenied
          ) {
            throw new AccessDeniedError();
          }
          return null;
        }),
      );
  }

  getEntryAndContact(id: string): Observable<{
    entry: ListItem<string> | null;
    contact: ListItem<string> | null;
    partner?: string;
    lead?: ListItem<string> | null;
    with?: ListItem<string> | null;
  } | null> {
    const key = new Key(id);
    const entity = this.getEntityCodeAndDriver(key);

    if (entity) {
      if (entity.code === Octopus.AbEntryEntityCode) {
        return this.getEntryAndContactFromAbEntry(id);
      }

      if (entity.code === Octopus.AppointmentEntityCode) {
        return this.getEntryAndContactFromAppointment(id);
      }

      if (entity.code === Octopus.TaskEntityCode) {
        return this.getEntryAndContactFromTask(id);
      }

      return this.getEntryAndContactFrom(id, entity.code, entity.configuration);
    }

    return of(null);
  }

  private getEntryAndContactFromTask(id: string): Observable<{
    entry: ListItem<string> | null;
    contact: ListItem<string> | null;
    partner?: string;
    lead: ListItem<string> | null;
    with: ListItem<string> | null;
  } | null> {
    const request: Octopus.TaskReadRequest = {
      Task: {
        Criteria: { SearchQuery: { Key: { $EQ: id } } },
        Scope: {
          Fields: {
            AbEntry: { Key: 1, DisplayValue: 1 },
            Lead: { Key: 1, DisplayValue: 1 },
            With: {
              Opportunity: { Key: 1, Objective: 1 },
              Case: { Key: 1, Subject: 1 },
            },
          },
        },
      },
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TaskResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code !== Octopus.ResponseStatusCode.Successful ||
            !result.Task?.Data?.length
          ) {
            return null;
          }
          const data = result.Task.Data[0];
          const abEntry = data.AbEntry;
          const lead = data.Lead;
          const withVal = data.With;
          const withKey =
            withVal?.Opportunity?.Key ?? withVal?.Case?.Key ?? null;
          const withDisplay =
            withVal?.Opportunity?.Objective ?? withVal?.Case?.Subject ?? null;

          return {
            entry: abEntry?.Key
              ? {
                  id: abEntry.Key,
                  name: abEntry.DisplayValue,
                }
              : null,
            contact: null,
            lead: lead?.Key
              ? ({
                  id: lead.Key,
                  name: lead.DisplayValue,
                } as unknown as ListItem<string>)
              : null,
            with: withKey
              ? ({ id: withKey, name: withDisplay } as ListItem<string>)
              : null,
          };
        }),
      );
  }

  getDisplayValue(id: string): Observable<string | null> {
    const key = new Key(id);
    const entity = this.getEntityCodeAndDriver(key);

    if (entity) {
      type EntityResponse = Partial<{
        [key in Octopus.EntityCode]: Octopus.DataResponse<Entity>;
      }> &
        Octopus.Response;

      const request: Octopus.EntityRequest = {
        [entity.code]: {
          Criteria: {
            SearchQuery: {
              Key: {
                $EQ: id,
              },
            },
          },
          Scope: {
            Fields: {
              [Octopus.EntityFields.DisplayValue]: 1,
            },
          },
        },
        Configuration: entity.configuration,
      };

      return this.http
        .post<EntityResponse>(
          `${this.context.api}${Octopus.Action.READ}`,
          request,
        )
        .pipe(
          map((result) => {
            if (
              result.Code === Octopus.ResponseStatusCode.Successful &&
              result[entity.code]?.Data?.length
            ) {
              return (
                (result[entity.code]?.Data[0]?.[
                  Octopus.EntityFields.DisplayValue
                ] as string) ?? null
              );
            }
            return null;
          }),
        );
    }

    return of(null);
  }

  private getReadRequest<T>(
    criteria: Octopus.Criteria<T>,
    types: Octopus.TypeScope,
    metadata = true,
  ): Octopus.EntityRequest {
    return {
      [this.entity]: {
        Criteria: criteria,
        Scope: {
          Fields: this.getFieldsForDefaultEntry(metadata),
          Types: types,
        },
        Options: {
          All: true,
        },
      },
      Configuration: this.readDriver,
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: {
        AbEntryKey: '2.0',
      },
    };
  }

  private getEntryAndContactFrom(
    id: string,
    code: Octopus.EntityCode,
    configuration: Octopus.RequestConfiguration,
  ) {
    type Entity = Pick<Octopus.Case, 'AbEntry' | 'Contact'> &
      Pick<Octopus.Opportunity, 'AbEntry' | 'Contact'> &
      Pick<Octopus.Task, Octopus.TaskFields.AbEntry> & {
        [Octopus.EntityFields.DisplayValue]?: string;
      };
    type EntityResponse = Partial<{
      [key in Octopus.EntityCode]: Octopus.DataResponse<Entity>;
    }> &
      Octopus.Response;

    const request: Octopus.EntityRequest = {
      [code]: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: id,
            },
          },
        },
        Scope: {
          Fields: {
            [Octopus.EntityFields.DisplayValue]: 1,
          },
          Types: {
            AbEntryObject: this.getKeyAndDisplayScope(false),
          },
        },
      },
      Configuration: configuration,
    };

    return this.http
      .post<EntityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result[code]?.Data?.length
          ) {
            const data = result[code]?.Data[0];
            if (data) {
              const display = data[Octopus.EntityFields.DisplayValue];
              return {
                entry: {
                  id: data.AbEntry.Key,
                  name: data.AbEntry.DisplayValue,
                } as ListItem<string>,
                contact: data.Contact
                  ? ({
                      id: data.Contact.Key,
                      name: data.Contact.DisplayValue,
                    } as ListItem<string>)
                  : null,
                with: { id, name: display ?? '' } as ListItem<string>,
              };
            }
          }
          return null;
        }),
      );
  }

  private getEntryAndContactFromAbEntry(id: string): Observable<{
    entry: ListItem<string> | null;
    contact: ListItem<string> | null;
    partner?: string;
  } | null> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: { Body: id, OrRelated: true },
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            DisplayValue: 1,
            Type: 1,
            PartnerKey: 1,
          },
        },
        Configuration: Octopus.AbEntryReadDriver,
      },
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.AbEntry?.Data?.length
          ) {
            const contact = result.AbEntry.Data.find(
              (entry) =>
                entry.Type === Octopus.AbEntryType.Contact && entry.Key === id,
            );
            const companyOrIndividual = result.AbEntry.Data.find(
              (entry) => entry.Type !== Octopus.AbEntryType.Contact,
            );

            return {
              entry: companyOrIndividual
                ? ({
                    id: companyOrIndividual.Key,
                    name: companyOrIndividual.DisplayValue,
                  } as ListItem<string>)
                : null,
              partner: companyOrIndividual?.PartnerKey,
              contact: contact
                ? ({
                    id: contact.Key,
                    name: contact.DisplayValue,
                  } as ListItem<string>)
                : null,
            };
          }
          return null;
        }),
      );
  }

  private getEntryAndContactFromAppointment(id: string): Observable<{
    entry: ListItem<string> | null;
    contact: ListItem<string> | null;
  } | null> {
    const request: Octopus.AppointmentReadRequest = {
      Appointment: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: id,
            },
          },
        },
        Scope: {
          Fields: {
            AbEntries: [
              {
                Key: 1,
                DisplayValue: 1,
              },
            ],
          },
        },
        Configuration: Octopus.AppointmentReadDriver,
      },
    };

    return this.http
      .post<Octopus.AppointmentResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Appointment?.Data?.length
          ) {
            const data = result.Appointment.Data[0];
            return data.AbEntries?.length === 1
              ? {
                  entry: {
                    id: data.AbEntries[0].Key,
                    name: data.AbEntries[0].DisplayValue,
                  } as ListItem<string>,
                  contact: null,
                }
              : null;
          }
          return null;
        }),
      );
  }

  private getValueAndFields(
    request: Octopus.EntityRequest,
    entity: string,
  ): Observable<EntityDetails> {
    return this.http
      .post<Octopus.EntityResponseWithMetadata>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        switchMap((response) => {
          if (
            response.Code === Octopus.ResponseStatusCode.Successful &&
            response[entity]
          ) {
            const data = response[entity]?.Data;

            if (data?.length) {
              const value = data[0];
              const mapper = new Octopus.MetadataMapper(entity);
              const metadata = mapper.mapMetadata(value);
              const fields = mapper.mapFields(metadata);

              return of({ value, fields });
            }
          }

          return this.metadata
            .getFields(this.entity)
            .pipe(map((fields) => ({ value: {}, fields })));
        }),
      );
  }

  abstract getFieldsForDefaultEntry(metadata: boolean): Octopus.Scope<Entity>;
}
